import { useMemo, useState, useEffect, useCallback, type FormEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import {
  ChevronDown,
  ChevronUp,
  ChevronDown as ChevronDownIcon,
  Plus,
  Search,
  Bed,
  Baby,
  Scissors,
  Activity,
  Zap,
  Brain,
  LayoutGrid,
  Pencil,
  Trash2,
  Calendar,
  Wrench,
  Unlock,
} from "lucide-react";
import {
  getLits,
  getLitsTypesList,
  getLitsStatutsList,
  getLitsDisponibles,
  createLit,
  updateLit,
  deleteLit,
  reserveLit,
  libererLit,
  maintenanceLit,
  type Lit,
  type LitReferentielItem,
} from "../../../api/etablissement";

/** Identifiant stable pour les actions API (id ou _id Mongo). */
function rowLitId(l: Lit): string {
  const r = l.id ?? (l as Record<string, unknown>)._id;
  return r != null && String(r) !== "" ? String(r) : "";
}
import { useAuth } from "../../../store/authEtablissementStore";
import { showToast } from "../../../components/ui/toastHotSonner";

/**
 * CRUD lits aligné sur docs/api/HELPEUR_USAGE_etablissement.md §4.3 + Annexe A.3.
 * Agrégats = dérivés de GET /lits/etablissement/:id (et optionnellement GET /lits/disponibles).
 */

const LIT_TYPE_META: Record<string, { label: string; Icon: typeof Bed }> = {
  standard: { label: "Lits standard", Icon: Bed },
  maternite: { label: "Maternité", Icon: Baby },
  pediatrie: { label: "Pédiatrie", Icon: Baby },
  chirurgie: { label: "Chirurgie", Icon: Scissors },
  soins_intensifs: { label: "Soins intensifs", Icon: Activity },
  soins: { label: "Soins intensifs", Icon: Activity },
  urgence: { label: "Urgences", Icon: Zap },
  psy: { label: "Psychiatrie", Icon: Brain },
  psychiatrie: { label: "Psychiatrie", Icon: Brain },
  isolement: { label: "Isolement", Icon: Bed },
  autre: { label: "Autre", Icon: Bed },
};

function formatLitTypeLabel(key: string): string {
  const k = key.toLowerCase().trim();
  if (LIT_TYPE_META[k]) return LIT_TYPE_META[k].label;
  return k
    .replace(/_/g, " ")
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function refCode(t: LitReferentielItem): string {
  return String(t.code ?? t.id ?? "").trim();
}

function refLabel(t: LitReferentielItem): string {
  return String(t.libelle ?? t.nom ?? t.code ?? t.id ?? "?");
}

function statutKey(l: Lit): string {
  return String(l.statut ?? "").toLowerCase().trim();
}

function isDisponible(l: Lit): boolean {
  return statutKey(l) === "disponible";
}

type TypeAgg = { key: string; label: string; total: number; disponibles: number };

function aggregateByType(lits: Lit[]): TypeAgg[] {
  const m = new Map<string, { total: number; disponibles: number }>();
  for (const l of lits) {
    const raw = l.type ?? (l.categorie as string | undefined) ?? "autre";
    const key = String(raw).trim() || "autre";
    const cur = m.get(key) ?? { total: 0, disponibles: 0 };
    cur.total += 1;
    if (isDisponible(l)) cur.disponibles += 1;
    m.set(key, cur);
  }
  return Array.from(m.entries()).map(([key, v]) => ({
    key,
    label: formatLitTypeLabel(key),
    ...v,
  }));
}

const ICON_BADGE_BASE =
  "group flex shrink-0 items-center justify-center border-2 border-[#0E7C62] bg-transparent transition-colors duration-200 hover:bg-[#0E7C62] hover:border-white";

const ICON_IN_BADGE = "shrink-0 text-[#0E7C62] transition-colors group-hover:text-white";

function LitTypeIcon({ typeKey }: { typeKey: string }) {
  const k = typeKey.toLowerCase().trim();
  const Icon = LIT_TYPE_META[k]?.Icon ?? Bed;
  return <Icon className={`size-5 ${ICON_IN_BADGE}`} aria-hidden />;
}

const inputClass =
  "w-full h-10 rounded-[10px] border-2 px-3 bg-white disabled:opacity-50 text-sm";
const inputStyle = { borderColor: "#D1E2DD" } as const;

function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
}) {
  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex min-h-dvh w-screen items-center justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="beds-modal-title"
    >
      <div
        className="relative z-[1] my-auto w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-[10px] border bg-white p-6 shadow-xl"
        style={{ borderColor: "#E5E7EB" }}
      >
        <div className="mb-4 flex items-center justify-between gap-4">
          <h2 id="beds-modal-title" className="text-lg font-semibold">
            {title}
          </h2>
          <button type="button" className="text-sm text-gray-500 hover:text-gray-800" onClick={onClose}>
            Fermer
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
}

export default function BedsPage() {
  const { establishment } = useAuth();
  const effectiveEtablissementId = useMemo(() => {
    const id = establishment?.id;
    if (typeof id !== "string") return "";
    const trimmed = id.trim();
    return trimmed;
  }, [establishment?.id]);
  const [query, setQuery] = useState("");
  const [period, setPeriod] = useState<"7" | "30" | "90">("30");
  const [showCrud, setShowCrud] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lits, setLits] = useState<Lit[]>([]);
  const [disponiblesApi, setDisponiblesApi] = useState<Lit[] | null>(null);
  const [types, setTypes] = useState<LitReferentielItem[]>([]);
  const [statuts, setStatuts] = useState<LitReferentielItem[]>([]);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editLit, setEditLit] = useState<Lit | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Lit | null>(null);
  const [actionLit, setActionLit] = useState<Lit | null>(null);
  const [actionKind, setActionKind] = useState<"reserve" | "liberer" | "maintenance" | null>(null);

  const createurDefault = useMemo(
    () => establishment?.email || establishment?.name || establishment?.id || "etablissement-dashboard",
    [establishment?.email, establishment?.name, establishment?.id]
  );

  const byType = useMemo(() => aggregateByType(lits), [lits]);
  const totalLits = lits.length;
  const availableFromList = useMemo(() => lits.filter(isDisponible).length, [lits]);
  const availableCount = disponiblesApi !== null ? disponiblesApi.length : availableFromList;
  const capacitySum = totalLits;
  const installedDisplay = totalLits;
  const totalDelta = 0;
  const availableDelta = 0;

  const loadLits = useCallback(
    (options?: { silent?: boolean }): Promise<void> => {
      const silent = options?.silent ?? false;
      if (!effectiveEtablissementId) {
        setLoading(false);
        return Promise.resolve();
      }
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      return getLits(effectiveEtablissementId)
        .then((res) => {
          if (res.ok) {
            setLits(res.data);
            return getLitsDisponibles({ etablissementId: effectiveEtablissementId }).then((d) => {
              if (d.ok) setDisponiblesApi(d.data);
              else setDisponiblesApi(null);
            });
          }
          if (silent) {
            showToast(res.message || "Impossible d’actualiser les lits.", "error");
          } else {
            setError(res.message || "Impossible de charger les lits.");
          }
        })
        .catch(() => {
          if (silent) showToast("Impossible d’actualiser les lits.", "error");
          else setError("Impossible de charger les lits.");
        })
        .finally(() => {
          if (!silent) setLoading(false);
        });
    },
    [effectiveEtablissementId]
  );

  useEffect(() => {
    void loadLits();
  }, [loadLits]);

  useEffect(() => {
    if (!effectiveEtablissementId) return;
    void getLitsTypesList().then((r) => {
      if (r.ok) setTypes(r.data);
    });
    void getLitsStatutsList().then((r) => {
      if (r.ok) setStatuts(r.data);
    });
  }, [effectiveEtablissementId]);

  const filteredLits = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return lits;
    return lits.filter((l) => {
      const hay = [
        l.numero,
        l.type,
        l.service,
        l.chambre,
        l.etage,
        l.statut,
        l.id,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [lits, query]);

  const handleDelete = async () => {
    const delId = deleteTarget ? rowLitId(deleteTarget) : "";
    if (!delId) return;
    setSavingId(delId);
    try {
      const res = await deleteLit(delId);
      if (!res.ok) {
        showToast(res.message || "Suppression impossible.", "error");
        return;
      }
      setDeleteTarget(null);
      await loadLits({ silent: true });
      showToast("Lit supprimé.", "success");
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-semibold">Gestion des lits</h1>
        <div className="py-12 text-center text-gray-600">Chargement des lits…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-semibold">Gestion des lits</h1>
        <div className="rounded-[10px] border p-6 text-center" style={{ borderColor: "#E5E7EB" }}>
          <p className="text-gray-700">{error}</p>
          <button
            type="button"
            onClick={() => loadLits()}
            className="mt-4 h-10 px-4 rounded-[10px] font-medium"
            style={{ backgroundColor: "#00553E", color: "#fff" }}
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-semibold">Gestion des lits</h1>

      <div className="flex items-center gap-3">
        <input
          placeholder="Rechercher (numéro, type, service, chambre…)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="flex-1 h-11 rounded-[10px] border-2 px-3 bg-white"
          style={{ borderColor: "#D1E2DD" }}
        />
        <span className="h-11 px-4 rounded-[10px] font-medium flex items-center justify-center bg-[#00553E] text-white">
          <Search size={18} />
        </span>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="px-3 py-2 rounded-[10px]" style={{ backgroundColor: "#EDEFF0" }}>
          Disponibilité des lits
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative z-40">
            <button
              type="button"
              className="h-10 px-4 rounded-[10px] border text-sm flex items-center gap-2 bg-white"
              style={{ borderColor: "#D1E2DD" }}
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("period-menu")?.classList.toggle("hidden");
              }}
            >
              {period} jours <ChevronDown size={16} />
            </button>
            <div
              id="period-menu"
              className="absolute right-0 mt-2 w-36 bg-white border rounded-[10px] shadow hidden z-50"
              style={{ borderColor: "#E5E7EB" }}
            >
              {(["7", "30", "90"] as const).map((p) => (
                <button
                  type="button"
                  key={p}
                  className="block w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                  onClick={() => {
                    setPeriod(p);
                    document.getElementById("period-menu")?.classList.add("hidden");
                  }}
                >
                  {p} jours
                </button>
              ))}
            </div>
          </div>
          <button
            type="button"
            className="h-10 px-4 rounded-[10px] font-medium text-sm border-2 border-transparent bg-[#00553E] text-white hover:bg-white hover:text-[#00553E] hover:border-[#00553E]"
            onClick={() => setShowCrud((v) => !v)}
          >
            {showCrud ? "Masquer la gestion" : "Gérer les lits"}
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-[10px] p-6 border" style={{ borderColor: "#E5E7EB" }}>
          <div className="font-semibold">Lits totaux</div>
          <p className="text-sm text-gray-500 mt-1">Nombre d’enregistrements lits par type (GET établissement)</p>
          <div className="mt-4 flex items-start justify-between gap-4">
            <div className="flex items-start gap-4 min-w-0">
              <div className={`${ICON_BADGE_BASE} size-14 rounded-xl`}>
                <LayoutGrid className={`size-7 ${ICON_IN_BADGE}`} aria-hidden />
              </div>
              <div>
                <div className="text-6xl font-semibold tabular-nums leading-none">{installedDisplay}</div>
                <div className="text-sm text-gray-500 mt-2">lits enregistrés</div>
                {availableCount > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    dont{" "}
                    <span className="font-semibold" style={{ color: "#00553E" }}>
                      {availableCount}
                    </span>{" "}
                    {disponiblesApi !== null ? "via GET disponibles ou statut" : "avec statut disponible"}
                  </p>
                )}
              </div>
            </div>
            <div className="text-sm font-semibold flex items-center gap-2 shrink-0" style={{ color: "#0E7C62" }}>
              <ChevronUp size={14} />
              {Math.abs(totalDelta)} %
            </div>
          </div>
          <div className="mt-6">
            {(() => {
              const q = query.trim().toLowerCase();
              const list = byType.filter(
                (c) =>
                  !q ||
                  c.label.toLowerCase().includes(q) ||
                  c.key.toLowerCase().includes(q)
              );
              if (q && list.length === 0) {
                return (
                  <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/80 py-8 text-center text-sm text-gray-500">
                    Aucun type ne correspond à votre recherche.
                  </div>
                );
              }
              if (list.length === 0) {
                return (
                  <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/80 py-8 text-center text-sm text-gray-500">
                    Aucun lit pour le moment.
                  </div>
                );
              }
              const capMax = Math.max(capacitySum, 1);
              return (
                <ul className="grid gap-3 sm:grid-cols-2">
                  {list.map((c) => {
                    const pct = Math.round((c.total / capMax) * 100);
                    return (
                      <li
                        key={`total-${c.key}`}
                        className="rounded-xl border bg-gradient-to-b from-white to-[#F0F7F5] px-4 py-3 shadow-sm"
                        style={{ borderColor: "#E5E7EB" }}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`${ICON_BADGE_BASE} size-10 rounded-lg`}>
                            <LitTypeIcon typeKey={c.key} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-baseline justify-between gap-2">
                              <span className="font-medium text-[#0B1F16]">{c.label}</span>
                              <span className="tabular-nums text-lg font-semibold" style={{ color: "#0B1F16" }}>
                                {c.total}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              <span className="font-medium" style={{ color: "#00553E" }}>
                                {c.disponibles}
                              </span>{" "}
                              disponible{c.disponibles > 1 ? "s" : ""}
                            </p>
                            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[#E5E7EB]">
                              <div
                                className="h-full rounded-full transition-all"
                                style={{ width: `${pct}%`, backgroundColor: "#0E7C62" }}
                              />
                            </div>
                            <p className="mt-1 text-xs text-gray-500">{pct}% du parc total</p>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              );
            })()}
          </div>
        </div>

        <div className="bg-white rounded-[10px] p-6 border" style={{ borderColor: "#E5E7EB" }}>
          <div className="font-semibold">Lits disponibles</div>
          <p className="text-sm text-gray-500 mt-1">Répartition par type (statut disponible)</p>
          <div className="mt-4 flex items-start justify-between gap-4">
            <div>
              <div className="text-6xl font-semibold tabular-nums">{availableCount}</div>
              <div className="text-sm text-gray-500 mt-1">lits libres (estimation)</div>
            </div>
            <div className="text-sm font-semibold flex items-center gap-2 shrink-0" style={{ color: "#DE3B3B" }}>
              <ChevronDownIcon size={14} />
              {Math.abs(availableDelta)} %
            </div>
          </div>
          <div className="mt-6">
            {(() => {
              const q = query.trim().toLowerCase();
              const list = byType.filter(
                (c) =>
                  !q ||
                  c.label.toLowerCase().includes(q) ||
                  c.key.toLowerCase().includes(q)
              );
              const max = Math.max(availableCount, 1);
              if (list.length === 0) {
                return (
                  <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/80 py-8 text-center text-sm text-gray-500">
                    Aucune donnée.
                  </div>
                );
              }
              return (
                <ul className="grid gap-3 sm:grid-cols-2">
                  {list.map((c) => {
                    const pct = Math.round((c.disponibles / max) * 100);
                    return (
                      <li
                        key={c.key}
                        className="rounded-xl border bg-gradient-to-b from-white to-[#F8FAF9] px-4 py-3 shadow-sm"
                        style={{ borderColor: "#E5E7EB" }}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`${ICON_BADGE_BASE} size-10 rounded-lg`}>
                            <LitTypeIcon typeKey={c.key} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-baseline justify-between gap-2">
                              <span className="font-medium text-[#0B1F16]">{c.label}</span>
                              <span className="tabular-nums text-lg font-semibold" style={{ color: "#00553E" }}>
                                {c.disponibles}
                              </span>
                            </div>
                            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[#E5E7EB]">
                              <div
                                className="h-full rounded-full"
                                style={{ width: `${pct}%`, backgroundColor: "#00553E" }}
                              />
                            </div>
                            <p className="mt-1 text-xs text-gray-500">{pct}% du total disponible</p>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              );
            })()}
          </div>
        </div>
      </div>

      {showCrud && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              className="h-10 px-4 rounded-[10px] font-medium text-sm inline-flex items-center gap-2 bg-[#00553E] text-white"
              onClick={() => setCreateOpen(true)}
            >
              <Plus size={18} aria-hidden />
              Ajouter un lit
            </button>
          </div>

          <div className="bg-white rounded-[10px] border overflow-x-auto" style={{ borderColor: "#E5E7EB" }}>
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="border-b bg-gray-50/80" style={{ borderColor: "#E5E7EB" }}>
                  <th className="text-left p-3 font-medium">Numéro</th>
                  <th className="text-left p-3 font-medium">Type</th>
                  <th className="text-left p-3 font-medium">Service</th>
                  <th className="text-left p-3 font-medium">Chambre</th>
                  <th className="text-left p-3 font-medium">Étage</th>
                  <th className="text-left p-3 font-medium">Statut</th>
                  <th className="text-right p-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLits.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-gray-500">
                      Aucun lit. Ajoutez un lit ou modifiez la recherche.
                    </td>
                  </tr>
                ) : (
                  filteredLits.map((l) => {
                    const id = rowLitId(l);
                    const busy = savingId === id;
                    return (
                      <tr key={id || `${l.numero}-${l.type}`} className="border-b" style={{ borderColor: "#F3F4F6" }}>
                        <td className="p-3 font-medium">{l.numero ?? "—"}</td>
                        <td className="p-3">{formatLitTypeLabel(String(l.type ?? ""))}</td>
                        <td className="p-3">{l.service ?? "—"}</td>
                        <td className="p-3">{l.chambre ?? "—"}</td>
                        <td className="p-3">{l.etage ?? "—"}</td>
                        <td className="p-3">
                          <span className="rounded-full px-2 py-0.5 text-xs bg-gray-100">{l.statut ?? "—"}</span>
                        </td>
                        <td className="p-3">
                          <div className="flex flex-wrap justify-end gap-1">
                            <button
                              type="button"
                              disabled={busy || !id}
                              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40"
                              title="Modifier"
                              onClick={() => setEditLit(l)}
                            >
                              <Pencil size={16} aria-hidden />
                            </button>
                            <button
                              type="button"
                              disabled={busy || !id}
                              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40"
                              title="Réserver"
                              onClick={() => {
                                setActionLit(l);
                                setActionKind("reserve");
                              }}
                            >
                              <Calendar size={16} aria-hidden />
                            </button>
                            <button
                              type="button"
                              disabled={busy || !id}
                              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40"
                              title="Libérer"
                              onClick={() => {
                                setActionLit(l);
                                setActionKind("liberer");
                              }}
                            >
                              <Unlock size={16} aria-hidden />
                            </button>
                            <button
                              type="button"
                              disabled={busy || !id}
                              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40"
                              title="Maintenance"
                              onClick={() => {
                                setActionLit(l);
                                setActionKind("maintenance");
                              }}
                            >
                              <Wrench size={16} aria-hidden />
                            </button>
                            <button
                              type="button"
                              disabled={busy || !id}
                              className="p-2 rounded-lg border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-40"
                              title="Supprimer"
                              onClick={() => setDeleteTarget(l)}
                            >
                              <Trash2 size={16} aria-hidden />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {createOpen && effectiveEtablissementId && (
        <CreateLitModal
          etablissementId={effectiveEtablissementId}
          types={types}
          statuts={statuts}
          createur={createurDefault}
          onClose={() => setCreateOpen(false)}
          onSuccess={() => {
            setCreateOpen(false);
            loadLits({ silent: true });
            showToast("Lit créé.", "success");
          }}
        />
      )}

      {editLit && rowLitId(editLit) && (
        <EditLitModal
          lit={editLit}
          types={types}
          statuts={statuts}
          onClose={() => setEditLit(null)}
          onSuccess={() => {
            setEditLit(null);
            loadLits({ silent: true });
            showToast("Lit mis à jour.", "success");
          }}
        />
      )}

      {deleteTarget && (
        <Modal title="Supprimer ce lit ?" onClose={() => setDeleteTarget(null)}>
          <p className="text-sm text-gray-600 mb-4">
            Numéro : <strong>{deleteTarget.numero ?? deleteTarget.id}</strong>
          </p>
          <div className="flex justify-end gap-2">
            <button type="button" className="h-10 px-4 rounded-[10px] border" onClick={() => setDeleteTarget(null)}>
              Annuler
            </button>
            <button
              type="button"
              className="h-10 px-4 rounded-[10px] font-medium text-white bg-red-700 hover:bg-red-800"
              onClick={() => void handleDelete()}
              disabled={!!savingId}
            >
              {savingId ? "Suppression…" : "Supprimer"}
            </button>
          </div>
        </Modal>
      )}

      {actionLit && rowLitId(actionLit) && actionKind && (
        <ActionLitModal
          kind={actionKind}
          lit={actionLit}
          onClose={() => {
            setActionLit(null);
            setActionKind(null);
          }}
          onSuccess={() => {
            setActionLit(null);
            setActionKind(null);
            loadLits({ silent: true });
            showToast("Action enregistrée.", "success");
          }}
        />
      )}

      <p className="text-sm text-gray-600">
        Mettre à jour la carte permet de visualiser la disponibilité des lits, des poches de sang, des médicaments et matériels.
      </p>
    </div>
  );
}

function CreateLitModal({
  etablissementId,
  types,
  statuts,
  createur,
  onClose,
  onSuccess,
}: {
  etablissementId: string;
  types: LitReferentielItem[];
  statuts: LitReferentielItem[];
  createur: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const defaultStatut =
    statuts.find((s) => refCode(s).toLowerCase() === "disponible") ?? statuts[0];
  const [numero, setNumero] = useState("");
  const [service, setService] = useState("");
  const [type, setType] = useState(() => (types.length ? refCode(types[0]) : "standard"));
  const [statut, setStatut] = useState(defaultStatut ? refCode(defaultStatut) : "disponible");
  const [chambre, setChambre] = useState("");
  const [etage, setEtage] = useState("");
  const [prixParJour, setPrixParJour] = useState(15000);
  const [prixParHeure, setPrixParHeure] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (types.length) setType(refCode(types[0]));
  }, [types]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (numero.trim().length < 1) {
      showToast("Le numéro du lit est requis.", "error");
      return;
    }
    if (service.trim().length < 2) {
      showToast("Le service (min. 2 caractères) est requis.", "error");
      return;
    }
    if (!type) {
      showToast("Choisissez un type de lit.", "error");
      return;
    }
    const ppj = Number(prixParJour);
    if (!Number.isFinite(ppj) || ppj < 0) {
      showToast("Indiquez un prix par jour valide (FCFA).", "error");
      return;
    }
    const pphRaw = prixParHeure.trim() === "" ? undefined : Number(prixParHeure);
    const prix: { prixParJour: number; prixParHeure?: number } = { prixParJour: ppj };
    if (pphRaw !== undefined && Number.isFinite(pphRaw) && pphRaw >= 0) {
      prix.prixParHeure = pphRaw;
    }
    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        etablissementId,
        numero: numero.trim(),
        service: service.trim(),
        type,
        createur,
        prix,
      };
      if (statut) body.statut = statut;
      if (chambre.trim()) body.chambre = chambre.trim();
      if (etage.trim()) body.etage = etage.trim();
      const res = await createLit(body);
      if (!res.ok) {
        showToast(res.message || "Création impossible.", "error");
        return;
      }
      onSuccess();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Nouveau lit" onClose={onClose}>
      <form onSubmit={(e) => void submit(e)} className="space-y-3">
        <div>
          <label className="block text-xs text-gray-600 mb-1">Numéro *</label>
          <input
            required
            className={inputClass}
            style={inputStyle}
            value={numero}
            onChange={(e) => setNumero(e.target.value)}
            placeholder="ex. LIT-101"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Service * (min. 2 car.)</label>
          <input
            required
            minLength={2}
            className={inputClass}
            style={inputStyle}
            value={service}
            onChange={(e) => setService(e.target.value)}
            placeholder="ex. Médecine interne"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Type *</label>
          <select
            className={inputClass}
            style={inputStyle}
            value={type}
            onChange={(e) => setType(e.target.value)}
          >
            {types.length === 0 ? (
              <option value="standard">standard</option>
            ) : (
              types.map((t) => (
                <option key={refCode(t) || refLabel(t)} value={refCode(t)}>
                  {refLabel(t)}
                </option>
              ))
            )}
          </select>
        </div>
        {statuts.length > 0 && (
          <div>
            <label className="block text-xs text-gray-600 mb-1">Statut</label>
            <select
              className={inputClass}
              style={inputStyle}
              value={statut}
              onChange={(e) => setStatut(e.target.value)}
            >
              {statuts.map((s) => (
                <option key={refCode(s) || refLabel(s)} value={refCode(s)}>
                  {refLabel(s)}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Prix par jour (FCFA) *</label>
            <input
              type="number"
              min={0}
              required
              className={inputClass}
              style={inputStyle}
              value={prixParJour}
              onChange={(e) => setPrixParJour(parseInt(e.target.value || "0", 10))}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Prix par heure (FCFA)</label>
            <input
              type="number"
              min={0}
              className={inputClass}
              style={inputStyle}
              value={prixParHeure}
              onChange={(e) => setPrixParHeure(e.target.value)}
              placeholder="Optionnel"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Chambre</label>
            <input className={inputClass} style={inputStyle} value={chambre} onChange={(e) => setChambre(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Étage</label>
            <input className={inputClass} style={inputStyle} value={etage} onChange={(e) => setEtage(e.target.value)} />
          </div>
        </div>
        <p className="text-xs text-gray-500">Créateur envoyé : {createur}</p>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="h-10 px-4 rounded-[10px] border" onClick={onClose}>
            Annuler
          </button>
          <button
            type="submit"
            disabled={saving}
            className="h-10 px-4 rounded-[10px] font-medium text-white bg-[#00553E] disabled:opacity-50"
          >
            {saving ? "Création…" : "Créer"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function EditLitModal({
  lit,
  types,
  statuts,
  onClose,
  onSuccess,
}: {
  lit: Lit;
  types: LitReferentielItem[];
  statuts: LitReferentielItem[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [numero, setNumero] = useState(lit.numero ?? "");
  const [service, setService] = useState(lit.service ?? "");
  const [type, setType] = useState(String(lit.type ?? "standard"));
  const [statut, setStatut] = useState(String(lit.statut ?? "disponible"));
  const [chambre, setChambre] = useState(lit.chambre ?? "");
  const [etage, setEtage] = useState(lit.etage ?? "");
  const [prixParJour, setPrixParJour] = useState(lit.prix?.prixParJour ?? 15000);
  const [prixParHeure, setPrixParHeure] = useState(
    lit.prix?.prixParHeure != null ? String(lit.prix.prixParHeure) : ""
  );
  const [saving, setSaving] = useState(false);

  const litId = rowLitId(lit);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!litId) return;
    if (service.trim().length < 2) {
      showToast("Le service (min. 2 caractères) est requis.", "error");
      return;
    }
    const ppj = Number(prixParJour);
    if (!Number.isFinite(ppj) || ppj < 0) {
      showToast("Indiquez un prix par jour valide (FCFA).", "error");
      return;
    }
    const pphRaw = prixParHeure.trim() === "" ? undefined : Number(prixParHeure);
    const prix: { prixParJour: number; prixParHeure?: number } = { prixParJour: ppj };
    if (pphRaw !== undefined && Number.isFinite(pphRaw) && pphRaw >= 0) {
      prix.prixParHeure = pphRaw;
    }
    setSaving(true);
    try {
      const res = await updateLit(litId, {
        numero: numero.trim(),
        service: service.trim(),
        type,
        statut,
        chambre: chambre.trim() || undefined,
        etage: etage.trim() || undefined,
        prix,
      });
      if (!res.ok) {
        showToast(res.message || "Mise à jour impossible.", "error");
        return;
      }
      onSuccess();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={`Modifier le lit ${lit.numero ?? litId}`} onClose={onClose}>
      <form onSubmit={(e) => void submit(e)} className="space-y-3">
        <div>
          <label className="block text-xs text-gray-600 mb-1">Numéro</label>
          <input className={inputClass} style={inputStyle} value={numero} onChange={(e) => setNumero(e.target.value)} />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Service *</label>
          <input
            required
            minLength={2}
            className={inputClass}
            style={inputStyle}
            value={service}
            onChange={(e) => setService(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Type</label>
          <select className={inputClass} style={inputStyle} value={type} onChange={(e) => setType(e.target.value)}>
            {types.length === 0 ? (
              <option value={type}>{type}</option>
            ) : (
              types.map((t) => (
                <option key={refCode(t) || refLabel(t)} value={refCode(t)}>
                  {refLabel(t)}
                </option>
              ))
            )}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Statut</label>
          <select className={inputClass} style={inputStyle} value={statut} onChange={(e) => setStatut(e.target.value)}>
            {statuts.length === 0 ? (
              <option value={statut}>{statut}</option>
            ) : (
              statuts.map((s) => (
                <option key={refCode(s) || refLabel(s)} value={refCode(s)}>
                  {refLabel(s)}
                </option>
              ))
            )}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Prix par jour (FCFA) *</label>
            <input
              type="number"
              min={0}
              required
              className={inputClass}
              style={inputStyle}
              value={prixParJour}
              onChange={(e) => setPrixParJour(parseInt(e.target.value || "0", 10))}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Prix par heure (FCFA)</label>
            <input
              type="number"
              min={0}
              className={inputClass}
              style={inputStyle}
              value={prixParHeure}
              onChange={(e) => setPrixParHeure(e.target.value)}
              placeholder="Optionnel"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Chambre</label>
            <input className={inputClass} style={inputStyle} value={chambre} onChange={(e) => setChambre(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Étage</label>
            <input className={inputClass} style={inputStyle} value={etage} onChange={(e) => setEtage(e.target.value)} />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="h-10 px-4 rounded-[10px] border" onClick={onClose}>
            Annuler
          </button>
          <button
            type="submit"
            disabled={saving}
            className="h-10 px-4 rounded-[10px] font-medium text-white bg-[#00553E] disabled:opacity-50"
          >
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ActionLitModal({
  kind,
  lit,
  onClose,
  onSuccess,
}: {
  kind: "reserve" | "liberer" | "maintenance";
  lit: Lit;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [patientId, setPatientId] = useState("");
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [motif, setMotif] = useState("");
  const [dureeEstimee, setDureeEstimee] = useState(60);
  const [saving, setSaving] = useState(false);

  const litId = rowLitId(lit);

  const run = async (e: FormEvent) => {
    e.preventDefault();
    if (!litId) return;
    if (motif.trim().length < 5) {
      showToast("Le motif doit contenir au moins 5 caractères.", "error");
      return;
    }
    setSaving(true);
    try {
      if (kind === "reserve") {
        if (!patientId.trim()) {
          showToast("Identifiant patient requis.", "error");
          setSaving(false);
          return;
        }
        const res = await reserveLit(litId, {
          patientId: patientId.trim(),
          motif: motif.trim(),
          ...(dateDebut ? { dateDebut } : {}),
          ...(dateFin ? { dateFin } : {}),
        });
        if (!res.ok) {
          showToast(res.message || "Réservation impossible.", "error");
          return;
        }
      } else if (kind === "liberer") {
        const res = await libererLit(litId, { motif: motif.trim() });
        if (!res.ok) {
          showToast(res.message || "Action impossible.", "error");
          return;
        }
      } else {
        const res = await maintenanceLit(litId, {
          motif: motif.trim(),
          dureeEstimee: Number(dureeEstimee) || 0,
        });
        if (!res.ok) {
          showToast(res.message || "Action impossible.", "error");
          return;
        }
      }
      onSuccess();
    } finally {
      setSaving(false);
    }
  };

  const titles = {
    reserve: "Réserver le lit",
    liberer: "Libérer le lit",
    maintenance: "Mettre en maintenance",
  };

  return (
    <Modal title={titles[kind]} onClose={onClose}>
      <form onSubmit={(e) => void run(e)} className="space-y-3">
        <p className="text-sm text-gray-600">
          Lit : <strong>{lit.numero ?? litId}</strong>
        </p>
        {kind === "reserve" && (
          <>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Patient ID *</label>
              <input
                className={inputClass}
                style={inputStyle}
                value={patientId}
                onChange={(e) => setPatientId(e.target.value)}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Date début</label>
                <input
                  type="datetime-local"
                  className={inputClass}
                  style={inputStyle}
                  value={dateDebut}
                  onChange={(e) => setDateDebut(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Date fin</label>
                <input
                  type="datetime-local"
                  className={inputClass}
                  style={inputStyle}
                  value={dateFin}
                  onChange={(e) => setDateFin(e.target.value)}
                />
              </div>
            </div>
          </>
        )}
        {kind === "maintenance" && (
          <div>
            <label className="block text-xs text-gray-600 mb-1">Durée estimée (minutes) *</label>
            <input
              type="number"
              min={1}
              className={inputClass}
              style={inputStyle}
              value={dureeEstimee}
              onChange={(e) => setDureeEstimee(parseInt(e.target.value || "0", 10))}
            />
          </div>
        )}
        <div>
          <label className="block text-xs text-gray-600 mb-1">Motif * (min. 5 car.)</label>
          <textarea
            required
            minLength={5}
            rows={3}
            className={`${inputClass} min-h-[80px] py-2`}
            style={inputStyle}
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="h-10 px-4 rounded-[10px] border" onClick={onClose}>
            Annuler
          </button>
          <button
            type="submit"
            disabled={saving}
            className="h-10 px-4 rounded-[10px] font-medium text-white bg-[#00553E] disabled:opacity-50"
          >
            {saving ? "Envoi…" : "Valider"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
